// @givmo/board — who presided at the meeting, the first row of "Who was there" (CBG-YR3-ROLES-
// PRESIDING; backend `board_minutes.md` §P2–§P9; frames `p1-who-presided`, `p2-presided-dialog`).
//
// ══ A RECORDED FACT, NEVER DERIVED ════════════════════════════════════════════════════════════
//
// One person presides per meeting (Q-P1), someone recorded as present: the database holds both, and
// the backend's refusal is shown verbatim if a write races one. The officer records it here. The
// board's ADOPTED duties only suggest whom — the person present who held, ON THE MEETING'S DAY, the
// title that presides (the orchestrator's C3: a secretary often records a meeting after a title changed
// hands) — and the Chair title never records anything. Nothing is recorded until Record is pressed.
//
// The minutes print who presided beside that person's capacity on the meeting's day ("Ruth Calloway
// (Chair, presiding)"). The dialog quotes those words from the server (`minutes_name`), never composes
// them, because the titles held that day are the backend's to read. Its options name each person by
// that day's capacity too (the read's facts, in the panel's words; the name alone where the read can't
// say), so its labels, its suggestion and the minutes read one day (review-138's F5).

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { isGivmoError, type OrgBoardMemberOut } from '@givmo/api-client';
import { Badge, Button, Dialog, ErrorMessage, Select, toast } from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { useBoardOperations } from '../../board-runtime';
import { capacityLine, capacityLineOf } from '../roster/officer-roles';
import { minutesRuleWords as rc11 } from '../minutes/minutes-rule-words';
import { decodePresiding, type PresidingRead } from './presiding-decode';
import { presidingWords as words } from './presiding-words';

/** A person as "Who was there" names them: "Ruth Calloway · Director · Chair". */
export function presidingLabel(seat: OrgBoardMemberOut): string {
  return `${seat.display_name} · ${capacityLine(seat)}`;
}

/**
 * A person as the dialog names them (review-138's F5): in the panel's words, with the titles held ON
 * THE MEETING'S DAY, from the presiding read, so a title that changed hands since reads the way the
 * suggestion and the minutes read it. Where the read can't say (before it answers, or for someone it
 * doesn't describe), the name alone, never today's titles.
 */
export function dayLabel(seat: OrgBoardMemberOut, read: PresidingRead | null): string {
  const capacity = read?.capacities.get(seat.id);
  return capacity === undefined
    ? seat.display_name
    : `${seat.display_name} · ${capacityLineOf(capacity.isDirector, capacity.titleLabels)}`;
}

export interface WhoPresidedRowProps {
  orgRef: string;
  meetingId: string;
  /** The seat recorded as presiding, from the attendance rows; null while nobody is. */
  presidingId: string | null;
  /** The whole roster, so the recorded person always resolves to a name. */
  roster: readonly OrgBoardMemberOut[];
  /** The people recorded as present, in the panel's order: the only people who can be chosen. */
  present: readonly OrgBoardMemberOut[];
  readOnly: boolean;
  /** Recorded: the attendance rows (and this row) read again. */
  onRecorded: () => void;
}

export function WhoPresidedRow({
  orgRef,
  meetingId,
  presidingId,
  roster,
  present,
  readOnly,
  onRecorded,
}: WhoPresidedRowProps) {
  const [open, setOpen] = useState(false);
  const seat = presidingId === null ? null : (roster.find((row) => row.id === presidingId) ?? null);

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-12 rounded-lg border border-border-subtle p-12"
      data-testid="attendance-presiding"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <div className="flex flex-wrap items-center gap-8">
          <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
            {rc11.whoPresided}
          </span>
          {presidingId === null ? <Badge tone="neutral">Not recorded</Badge> : null}
        </div>
        {seat !== null ? (
          <span
            className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
            data-testid="attendance-presiding-person"
          >
            {presidingLabel(seat)}
          </span>
        ) : null}
      </div>
      {!readOnly ? (
        <Button
          appearance="saas"
          tone="secondary"
          onClick={() => setOpen(true)}
          testId="attendance-presiding-record"
        >
          {presidingId === null ? 'Record' : 'Change'}
        </Button>
      ) : null}

      {open ? (
        <PresidingDialog
          orgRef={orgRef}
          meetingId={meetingId}
          presidingId={presidingId}
          present={present}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            onRecorded();
          }}
        />
      ) : null}
    </div>
  );
}

function PresidingDialog({
  orgRef,
  meetingId,
  presidingId,
  present,
  onClose,
  onSaved,
}: {
  orgRef: string;
  meetingId: string;
  presidingId: string | null;
  present: readonly OrgBoardMemberOut[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  const readOptions = operations.officerGetBoardMeetingPresidingOptions({
    path: { org_ref: orgRef, meeting_id: meetingId },
  });
  // Read fresh when the dialog opens: who is present may have changed a moment ago in this panel.
  const readQuery = useQuery({ ...readOptions, select: decodePresiding, staleTime: 0 });
  const read = readQuery.data ?? null;

  const presentIds = new Set(present.map((seat) => seat.id));
  // The duties' suggestion pre-selects only while nothing is recorded, and only someone listed here.
  const suggestion =
    presidingId === null && read?.suggestion != null && presentIds.has(read.suggestion.seatId)
      ? read.suggestion
      : null;
  const [chosen, setChosen] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  // Until the officer chooses, the field holds what is recorded, else the suggestion — derived, so a
  // read that arrives after the dialog opened still pre-selects.
  const value = touched ? chosen : (presidingId ?? suggestion?.seatId ?? null);
  const minutesName = value !== null ? (read?.minutesNames.get(value) ?? null) : null;
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      await operations.officerRecordBoardMeetingPresiding({
        path: { org_ref: orgRef, meeting_id: meetingId },
        body: { board_member_id: value },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success('Recorded.');
      void queryClient.invalidateQueries({ queryKey: readOptions.queryKey });
      onSaved();
    },
    onError: (caught) => {
      setError(isGivmoError(caught) ? caught.message : words.recordFailed);
    },
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !save.isPending && onClose()}>
      <Dialog.Content
        aria-label={words.dialogTitle}
        className="flex flex-col gap-16"
        testId="presiding-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {words.dialogTitle}
        </Dialog.Title>

        <Select
          aria-label={words.dialogTitle}
          appearance="saas"
          placeholder="Choose a seat"
          value={value}
          onValueChange={(next) => {
            setTouched(true);
            setChosen(next);
          }}
          options={present.map((seat) => ({ value: seat.id, label: dayLabel(seat, read) }))}
          testId="presiding-seat"
        />
        {suggestion !== null && value === suggestion.seatId ? (
          <FieldNote testId="presiding-suggested">
            {suggestion.actKey === 'presides'
              ? words.suggestedPresides(suggestion.titleLabel)
              : words.suggestedPresidesInAbsence(suggestion.titleLabel)}
          </FieldNote>
        ) : null}
        <FieldNote testId="presiding-minutes-line">
          {minutesName !== null
            ? `${words.minutesWillSay(minutesName)} ${words.onlyPresent}`
            : words.onlyPresent}
        </FieldNote>

        {error !== null ? <ErrorMessage testId="presiding-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={save.isPending}
            onClick={onClose}
            testId="presiding-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={save.isPending}
            disabled={value === null || save.isPending}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            testId="presiding-save"
          >
            Record
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
